import ReplyWithTextFeatures from '../contract/ReplyWithTextFeatures';
import insertContentIntoEditor from 'owa-mail-compose-actions/lib/plugins/contentOperation/insertContentIntoEditor';
import { createDefaultComposeFormatWrapper } from 'owa-mail-compose-actions/lib/utils/getDefaultComposeContentBlock';
import { ComposeViewState } from 'owa-mail-compose-store';
import { getSelectedTableView } from 'owa-mail-list-store';
import { isDeepLink } from 'owa-popout';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { trace } from 'owa-trace';
import { ContentPosition } from 'roosterjs-editor-types';
import {
    findInlineComposeViewState,
    lazyReplyToMessageAndCreateViewState,
} from 'owa-mail-compose-actions';

const QUOTE_CLASS = 'userQuote';
const SMART_REPLY_CLASS = 'smartReply';

export default async function replyWithText(
    itemId: string,
    conversationId: string,
    selection: string,
    feature: ReplyWithTextFeatures
): Promise<ComposeViewState> {
    let viewState: ComposeViewState;

    const existingViewState = findInlineComposeViewState(conversationId);
    if (existingViewState) {
        // If the compose view state already exists, this means the compose window is open
        viewState = existingViewState;
    } else {
        // Otherwise, create a new reply
        const replyToMessageAndCreateViewState = await lazyReplyToMessageAndCreateViewState.import();
        viewState = await replyToMessageAndCreateViewState({
            referenceItemOrId: itemId,
            isReplyAll: true,
            useFullCompose: getListViewType() == ReactListViewType.Message || isDeepLink(),
            conversationId: conversationId,
            instrumentationContexts: [],
            useCortanaSchedulingAssistant: feature == ReplyWithTextFeatures.SmartTimeWithCortana,
        });
    }

    // Once the view state is ready, insert the quoted content into the editor
    if (viewState) {
        await insertContentIntoEditor(
            viewState,
            getHtmlContent(selection, feature) /* htmlContent */,
            '\n' + selection + '\n' /* plainTextContent */,
            existingViewState
                ? ContentPosition.SelectionStart
                : ContentPosition.Begin /* insertContentPosition */,
            getEventName(feature) /* changeSource */
        );
    }

    return viewState;
}

function getEventName(feature: ReplyWithTextFeatures): string {
    switch (feature) {
        case ReplyWithTextFeatures.UserMarkup:
            return 'UserMarkup';
        case ReplyWithTextFeatures.SmartReply:
        case ReplyWithTextFeatures.SmartTimeWithCortana:
            return 'SmartReply';
        default:
            trace.warn('replyWithText_getEventName: Unsupported feature inputted');
            return '';
    }
}

function createElementForText(feature: ReplyWithTextFeatures, textForMarkup: string): HTMLElement {
    switch (feature) {
        case ReplyWithTextFeatures.UserMarkup:
            return createQuoteTextElement(textForMarkup);
        case ReplyWithTextFeatures.SmartReply:
        case ReplyWithTextFeatures.SmartTimeWithCortana:
            return createSmartReplyElement(textForMarkup);
        default:
            trace.warn('[createHtmlForText] Unsupported feature');
            return null;
    }
}

function createQuoteTextElement(quoteText: string): HTMLElement {
    // TODO: Reference the same function as the blockquote button
    // in the ribbon (VSO 36144)
    const quoteElement = document.createElement('blockquote');
    quoteElement.className = QUOTE_CLASS;
    quoteElement.style.borderLeft = '3px solid #C8C8C8';
    quoteElement.style.paddingLeft = '1ex';
    quoteElement.style.marginLeft = '0.8ex';
    quoteElement.style.color = '#666666';
    quoteElement.innerText = quoteText;

    const elementWrapper = document.createElement('div');
    elementWrapper.appendChild(document.createElement('br'));
    elementWrapper.appendChild(quoteElement);
    elementWrapper.appendChild(document.createElement('br'));

    return elementWrapper;
}

function createSmartReplyElement(smartReplyText: string): HTMLElement {
    const smartReplyElement = document.createElement('span');
    smartReplyElement.className = SMART_REPLY_CLASS;
    smartReplyElement.innerText = smartReplyText;

    return smartReplyElement;
}

function getListViewType(): ReactListViewType {
    let listViewType = null;
    const tableView = getSelectedTableView();
    if (tableView?.tableQuery) {
        listViewType = tableView.tableQuery.listViewType;
    }
    return listViewType;
}

function getHtmlContent(selection: string, feature: ReplyWithTextFeatures): HTMLElement {
    const textDiv = createDefaultComposeFormatWrapper();
    const selectionHtmlNode = createElementForText(feature, selection);
    textDiv.appendChild(selectionHtmlNode);
    return textDiv;
}
